import SEO from "@/components/SEO";
import {
  CTAButton,
  H2,
  HeroH1,
  P,
  SeoPageShell,
} from "../seo/SeoPageLayout";
import AnalysisExampleBlock from "@/components/seo/AnalysisExampleBlock";
import CrossLinkFooter from "@/components/seo/AnalysisCrossLinkFooter";

const Bullet = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <li className="mt-3 text-[17px] leading-[1.7] text-white/75">
    <span className="text-white font-semibold">{title}</span> {children}
  </li>
);

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How do you tell fair criticism from a character attack in a text?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Fair criticism is about the trait, not the moment. 'You're so selfish' attacks who you are; 'that was a selfish thing to do' addresses what happened. One is permanent, one is specific.",
      },
    },
    {
      "@type": "Question",
      name: "What does it mean when criticism 'arrives with no exit'?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Real feedback leaves room to respond, explain, or improve. Character-attack criticism is phrased as a closed verdict — nothing you say will change the label.",
      },
    },
    {
      "@type": "Question",
      name: "Why does the same criticism keep escalating instead of resolving?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "If pointing out a specific issue always ends with a comment on your character, worth, or history, the specific issue was never really the point.",
      },
    },
    {
      "@type": "Question",
      name: "Is it a red flag if the criticism is the same no matter what I did?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. When the 'criticism' is roughly the same regardless of what actually happened, that's a sign it was never really about the behavior.",
      },
    },
  ],
};

const CriticismPatternsInTextMessages = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Criticism Patterns in Text Messages: How to Spot Them"
        description="Paste any message. See if it's fair feedback or a criticism pattern — named in seconds, mechanism explained. Free, private, instant."
        path="/analysis/criticism-patterns-in-text-messages"
        jsonLd={FAQ_SCHEMA}
      />

      <HeroH1>How to Spot Criticism Patterns in Text Messages</HeroH1>

      <H2>The Quick Diagnostic</H2>
      <P>
        Fair criticism and character-attack criticism can look almost identical on the surface.
        Here's what actually separates them:
      </P>
      <ul className="list-none pl-0 mt-2">
        <Bullet title="It's about the trait, not the moment.">
          "You're so selfish" attacks who you are. "That was a selfish thing to do" addresses what
          happened. One is permanent, one is specific.
        </Bullet>
        <Bullet title="It arrives with no exit.">
          Real feedback leaves room for you to respond, explain, or improve. Character-attack
          criticism is often phrased as a closed verdict — nothing you say will change the label.
        </Bullet>
        <Bullet title="It escalates instead of resolving.">
          If pointing out a specific issue somehow always ends with a comment on your character,
          worth, or history, the specific issue was never really the point.
        </Bullet>
        <Bullet title="It shows up regardless of the actual mistake.">
          Watch for a pattern where the "criticism" is roughly the same regardless of what
          actually happened — that's a sign it was never really about the behavior.
        </Bullet>
      </ul>

      <H2>What it looks like in a real exchange</H2>
      <AnalysisExampleBlock
        turns={[
          { speaker: "them", text: "You forgot to pick up the dry cleaning again." },
          { speaker: "you", text: "I'm sorry, it slipped my mind — I'll grab it tomorrow." },
          {
            speaker: "them",
            tone: "toxic",
            text: "Of course you forgot. You're so unreliable, you never think about anyone but yourself.",
          },
        ]}
        mechanismLabel="Pattern: Behavior → Character Escalation"
        mechanismDescription="A single missed errand became a verdict about your entire character. The specific, fixable thing disappeared, replaced by something that isn't fixable at all — 'who you are.'"
      />

      <H2>The breakdown</H2>
      <P>
        Genuine feedback and character-attack criticism can use almost identical vocabulary —
        that's what makes this pattern hard to name in the moment. The tell isn't the words, it's
        the <em>scope</em>. Fair feedback is bounded: it's about one action, at one time, with a
        path forward. Character-attack criticism is unbounded: it reaches for your identity, your
        history, your worth — territory a single missed errand has no business touching. The
        disproportion between the trigger and the response is the signal, not the vocabulary used
        to deliver it.
      </P>

      <H2>See it in your own conversation</H2>
      <P>
        You just read a fictional example. If a real exchange has been sitting with you, paste it
        into the analyzer — no signup required for your first look.
      </P>
      <CTAButton />

      <CrossLinkFooter current="criticism" />
    </SeoPageShell>
  );
};

export default CriticismPatternsInTextMessages;
